/**
 * Country comparison, including the country disagreement between the two source
 * files: a newsletter's country is not always the partner's registered country.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import { BarsChart } from '../components/charts'
import {
  Card, Tile, Badge, Empty, QueryState, Notice, fmtInt, downloadCsv, AlumniSwitch,
} from '../components/ui'
import { useRelabel } from '../lib/terms'

export default function CountryComparison() {
  const t = useRelabel()
  const [withAlumni, setWithAlumni] = useState(false)
  const query = useApi(
    ['stats-countries', withAlumni],
    `stats/countries${withAlumni ? '?include_alumni=1' : ''}`
  )

  return (
    <PageHeader
      title="Country Comparison"
      sub="Partner units, output and where the two source files disagree"
      actions={
        <AlumniSwitch checked={withAlumni} onChange={setWithAlumni} where="partner units" />
      }
    >
      <QueryState query={query}>
        {(d) => {
          const active = d.rows.filter((r) => r.newsletter_count > 0)
          const mismatchRows = d.mismatch.reduce((a, r) => a + r.n, 0)

          return (
            <div className="col">
              <div className="grid grid-4">
                <Tile label="Entries with updates" value={fmtInt(active.length)} />
                <Tile label="Updates" value={fmtInt(active.reduce((a, r) => a + r.newsletter_count, 0))} />
                <Tile
                  label="Country disagreements"
                  value={fmtInt(mismatchRows)}
                  tone="warn"
                  note={`${d.mismatch.length} distinct pairings`}
                />
                <Tile
                  label="Updates per partner unit"
                  value={
                    active.reduce((a, r) => a + r.partner_count, 0)
                      ? Math.round(
                          active.reduce((a, r) => a + r.newsletter_count, 0) /
                            active.reduce((a, r) => a + r.partner_count, 0)
                        )
                      : '—'
                  }
                  note="average across all entries"
                />
              </div>

              <Card title="Updates and Partner Units Side by Side">
                <BarsChart
                  data={active.slice(0, 15)}
                  x="country_name"
                  series={[
                    { key: 'newsletter_count', label: 'Updates' },
                    { key: 'partner_count', label: 'Partner Units' },
                  ]}
                  horizontal
                  height={380}
                />
              </Card>

              <Notice tone="info" icon="info" title="Why the same partner unit appears under two countries">
                Both source files carry a Country column and they do not always agree — a partner
                unit registered in one country may write from another, or be recorded against the home
                office. Rather than pick a winner, the schema keeps both:{' '}
                <code>partners.country_id</code> for the registry value and{' '}
                <code>newsletters.country_id</code> for the value on each update. This table
                shows every pairing that differs.
              </Notice>

              <Card
                title="Registry Country vs Update Country"
                sub={`${fmtInt(mismatchRows)} updates across ${d.mismatch.length} pairings`}
                actions={
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      downloadCsv('country-mismatches.csv', d.mismatch, [
                        { key: 'partner_country_name', label: 'Partner unit registered in' },
                        { key: 'country_name', label: 'Update recorded' },
                        { key: 'n', label: 'Updates' },
                      ])
                    }
                  >
                    ↓ CSV
                  </button>
                }
              >
                {d.mismatch.length ? (
                  <div className="table-wrap flush">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>{t('Partner unit registered in')}</th>
                          <th>Update recorded</th>
                          <th className="right">Updates</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {d.mismatch.map((r, i) => (
                          <tr key={i}>
                            <td>{r.partner_country_name ?? <span className="subtle">none</span>}</td>
                            <td>
                              <Badge tone="warn">{r.country_name ?? 'none'}</Badge>
                            </td>
                            <td className="num">{fmtInt(r.n)}</td>
                            <td>
                              <Link className="btn btn-sm" to="/newsletters?country_mismatch=1">
                                Browse
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty title="Every update agrees with its partner unit's registry country" />
                )}
              </Card>

              <Card
                title="All Entries"
                actions={
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      downloadCsv('country-comparison.csv', d.rows, [
                        { key: 'country_name', label: 'Country' },
                        { key: 'kind', label: 'Kind' },
                        { key: 'partner_count', label: 'Partner Units' },
                        { key: 'newsletter_count', label: 'Updates' },
                        { key: 'highlighted_count', label: 'Highlighted' },
                        { key: 'last_newsletter', label: 'Latest' },
                      ])
                    }
                  >
                    ↓ CSV
                  </button>
                }
              >
                <div className="table-wrap flush">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Country</th>
                        <th>Kind</th>
                        <th className="right">{t('Partner Units')}</th>
                        <th className="right">Updates</th>
                        <th className="right">{t('Per partner unit')}</th>
                        <th className="right">Highlighted</th>
                        <th>Latest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.rows.map((r) => (
                        <tr key={r.country_id}>
                          <td>
                            <Link to={`/countries/${r.country_id}`}>
                              {r.flag_emoji ? <span aria-hidden="true">{r.flag_emoji} </span> : null}
                              {r.country_name}
                            </Link>
                          </td>
                          <td>
                            <Badge>{r.kind}</Badge>
                          </td>
                          <td className="num">{fmtInt(r.partner_count)}</td>
                          <td className="num">{fmtInt(r.newsletter_count)}</td>
                          <td className="num">
                            {r.partner_count ? Math.round(r.newsletter_count / r.partner_count) : '—'}
                          </td>
                          <td className="num">{fmtInt(r.highlighted_count)}</td>
                          <td className="small nowrap">{r.last_newsletter ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
